import { Component, inject } from '@angular/core';
import { first } from 'rxjs';
import { DynamicPageDcBaseComponent } from '../dynamic-page-dc-base/dynamic-page-dc-base.component';
import { DocumentsService } from '../../../../../services/documents/documents.service';
import { Document } from '../../../../../models/document';

export interface DocumentListEntry {
  id: string;
  filename: string;
  label: string;
  icon: string;
}

@Component({
  selector: 'lib-dynamic-page-dc-document-list',
  standalone: false,
  templateUrl: './dynamic-page-dc-document-list.component.html',
  styleUrl: './dynamic-page-dc-document-list.component.scss'
})
export class DynamicPageDcDocumentListComponent extends DynamicPageDcBaseComponent {
  protected documentsService = inject(DocumentsService);

  elementId = 'document-list';
  fields = {
    title: 'Documents',
    titleIcon: 'fa-solid fa-circle-down',
    maxHeight: 400,
    documents: [] as DocumentListEntry[],
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    backgroundColor: null
  };

  availableDocuments: Document[] = [];
  selectedDocumentId: string = '';

  iconOptions = [
    { class: 'fa-regular fa-file-pdf', name: 'PDF' },
    { class: 'fa-regular fa-file-lines', name: 'Document' },
    { class: 'fa-regular fa-file-word', name: 'Word' },
    { class: 'fa-regular fa-file-excel', name: 'Excel' },
    { class: 'fa-regular fa-file-image', name: 'Image' },
    { class: 'fa-regular fa-file-zipper', name: 'Archive' },
    { class: 'fa-regular fa-file', name: 'File' },
    { class: 'fa-regular fa-book', name: 'Guide' },
    { class: 'fa-regular fa-circle-info', name: 'Info' },
    { class: 'fa-solid fa-circle-down', name: 'Download' }
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.mode === 'editor') {
      this.documentsService
        .getDocuments()
        .pipe(first())
        .subscribe(docs => {
          this.availableDocuments = [...docs].sort((a, b) =>
            this.getDocumentLabel(a).localeCompare(this.getDocumentLabel(b))
          );
        });
    }
  }

  get selectableDocuments(): Document[] {
    return this.availableDocuments.filter(d => !this.element.fields.documents.some(entry => entry.id === d.id));
  }

  getDocumentLabel(doc: Document): string {
    return doc.metaData?.description && doc.metaData.description !== 'undefined'
      ? doc.metaData.description
      : doc.filename;
  }

  addDocument(): void {
    const doc = this.availableDocuments.find(d => d.id === this.selectedDocumentId);
    if (!doc) {
      return;
    }
    this.element.fields.documents.push({
      id: doc.id,
      filename: doc.filename,
      label: this.getDocumentLabel(doc),
      icon: doc.contentType?.includes('pdf') ? 'fa-regular fa-file-pdf' : 'fa-regular fa-file-lines'
    });
    this.selectedDocumentId = '';
  }

  removeDocument(index: number): void {
    this.element.fields.documents.splice(index, 1);
  }

  moveDocument(index: number, direction: number): void {
    const target = index + direction;
    if (target < 0 || target >= this.element.fields.documents.length) {
      return;
    }
    const documents = this.element.fields.documents;
    [documents[index], documents[target]] = [documents[target], documents[index]];
  }

  downloadDocument(entry: DocumentListEntry): void {
    if (this.edit) {
      return;
    }
    const url = this.documentsService.getDocumentUrl(entry.id) + '?contentDisposition=attachment';
    window.open(url, '_blank');
  }
}
